import React from 'react';
import { getPaginationModel } from './paginationUtils';

const CookbookManager = ({ cookbooks, allCookbooks, onEdit, onDelete, searchTerm, onSearchChange, page, onPageChange, selectedCookbookIds, onCookbookSelection, unselectAllCookbooks, searchInputRef }) => {
  const sortedCookbooks = [...cookbooks].sort((a, b) => a.title.localeCompare(b.title));
  const pageSize = 5;
  const totalPages = Math.ceil(sortedCookbooks.length / pageSize);
  const paginatedCookbooks = sortedCookbooks.slice((page - 1) * pageSize, page * pageSize);
  const paginationModel = getPaginationModel(page, totalPages);

  const emptyRows = pageSize - paginatedCookbooks.length;

  return (
    <div>
      {/* Show controls if there are selected items OR if there are list items OR if searching */}
      {(cookbooks.length > 0 || searchTerm.length > 0 || selectedCookbookIds.length > 0) && (
        <>
          <div className="recipe-filter-controls" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: selectedCookbookIds.length > 0 ? '10px' : '0' }}>
              {selectedCookbookIds.length > 0 && <button title="Unselect all cookbooks" onClick={unselectAllCookbooks} className="unselect-button">Unselect</button>}
              <div className={`search-input-container ${selectedCookbookIds.length > 0 ? 'collapsed' : 'expanded'}`}>
                <input
                  ref={searchInputRef}
                  data-tour="cookbooks-search"
                  type="text"
                  placeholder="Search Cookbooks"
                  value={searchTerm}
                  onChange={e => onSearchChange(e.target.value)}
                />
                {searchTerm && <button onClick={() => { onSearchChange(''); searchInputRef.current.focus(); }} className="clear-input-button" title="Clear search"><i className="material-icons">close</i></button>}
              </div>
            </div>
            {selectedCookbookIds.length > 0 && (
              <div className="selected-items">
                {selectedCookbookIds.map(item => {
                  const id = typeof item === 'object' && item !== null ? item.id : item;
                  const operation = typeof item === 'object' && item !== null ? item.operation : 'OR';
                  const actualList = allCookbooks || cookbooks;
                  const cookbook = actualList.find(c => c.id === id);
                  if (!cookbook) return null;
                  return (
                    <div key={id} className="selected-item" style={{ backgroundColor: operation === 'NOT' ? '#f44336' : '#2196F3' }}>
                      <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{operation}:</span>
                      {cookbook.title}
                      <button onClick={() => onCookbookSelection(id, operation)} className="remove-item-button"><i className="material-icons">close</i></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
      {cookbooks.length === 0 && searchTerm.length === 0 && selectedCookbookIds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No cookbooks found.<br />Add one by using the + button.</div>
      ) : null}
      {paginatedCookbooks.length === 0 && searchTerm.length > 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No cookbooks found for "{searchTerm}".</div>
      ) : (
        <>
          <ul className="cookbook-list" data-tour="cookbooks-list">
            {paginatedCookbooks.map((cookbook) => {
              const activeFilter = selectedCookbookIds.find(item => (typeof item === 'object' ? item.id : item) === cookbook.id);
              const isSelected = !!activeFilter;

              return (
                <li key={cookbook.id} data-tour={cookbook.isTemporary ? `cookbook-${cookbook.id}` : null} className={isSelected ? 'selected' : ''} style={{ cursor: 'default' }}>
                  <span onClick={() => onCookbookSelection(cookbook.id)} style={{ flexGrow: 1, cursor: 'pointer' }}>{cookbook.title}</span>

                  <div className="item-actions" style={{ marginRight: '10px' }}>
                    {/* OR Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onCookbookSelection(cookbook.id, 'OR'); }}
                      className="icon-button"
                      title="Include (OR)"
                      style={{ color: (activeFilter?.operation === 'OR') ? '#2196F3' : '#555' }}
                    >
                      <i className="material-icons">call_split</i>
                    </button>

                    {/* NOT Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onCookbookSelection(cookbook.id, 'NOT'); }}
                      className="icon-button"
                      title="Exclude (NOT)"
                      style={{ color: (activeFilter?.operation === 'NOT') ? '#f44336' : '#555' }}
                    >
                      <i className="material-icons">remove_circle</i>
                    </button>
                  </div>

                  <div className="item-actions">
                    <button onClick={(e) => { e.stopPropagation(); onEdit(cookbook); }} className="icon-button" title="Edit cookbook"><i className="material-icons">edit</i></button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(cookbook.id); }} className="icon-button delete-button" title="Delete cookbook"><i className="material-icons">delete</i></button>
                  </div>
                </li>
              );
            })}
            {page === totalPages && totalPages > 1 && emptyRows > 0 && [...Array(emptyRows)].map((_, index) => (
              <li key={`empty-${index}`} style={{ visibility: 'hidden' }}>
                &nbsp;
                <div className="item-actions empty-row-actions">
                  <button className="icon-button" title="Edit cookbook"><i className="material-icons">edit</i></button>
                  <button className="icon-button delete-button" title="Delete cookbook"><i className="material-icons">delete</i></button>
                </div>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button onClick={() => onPageChange(page - 1)} disabled={page === 1} className="icon-button" title="Previous page"><i className="material-icons">keyboard_arrow_left</i></button>
              <div className="page-numbers">
                {paginationModel.map((item, index) => (
                  <button key={index} onClick={() => onPageChange(item)} disabled={item === '...' || item === page} className={item === page ? 'active' : ''} title={`Go to page ${item}`}>
                    {item}
                  </button>
                ))}
              </div>
              <button onClick={() => onPageChange(page + 1)} disabled={paginatedCookbooks.length < pageSize || page * pageSize >= cookbooks.length} className="icon-button" title="Next page"><i className="material-icons">keyboard_arrow_right</i></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CookbookManager;